import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { FaArrowRight, FaChevronLeft, FaChevronRight, FaStar, FaPlus, FaPlay } from 'react-icons/fa';
import { API_BASE_URL } from '../config/apiConfig';

/* ─── Dental Services Data ────────────────────────────────────── */
const SERVICES = [
  { id: 'dental-implants', icon: '🦷', title: 'Best Dental Implants', description: 'Internationally trained implantologists using Nobel Biocare (Switzerland) implants for natural-looking, permanent teeth replacement.' },
  { id: 'single-sitting-rct', icon: '⚡', title: 'Single Sitting RCT', description: 'Quick, painless, single-visit root canal therapy using advanced rotary endodontic technology and apex locators.' },
  { id: 'braces-and-orthodontics', icon: '✨', title: 'Braces & Invisalign', description: 'Ultra-modern low-friction painless fast braces, tooth-colored ceramic braces, and invisible clear aligners.' },
  { id: '3d-intraoral-scanner', icon: '🔍', title: '3Shape 3D Scanner', description: 'Gag-free, 100% accurate digital intraoral 3D scanning available in less than 0.001% of dental clinics globally.' },
  { id: 'digital-opg-xray', icon: '🩻', title: 'Carestream Digital OPG', description: 'In-house Kodak Carestream digital panoramic radiology for precise jawbone and full-mouth diagnostic clarity.' },
  { id: 'cosmetic-dentistry', icon: '💎', title: 'Cosmetic Dentistry', description: 'In-office teeth whitening, ultra-thin porcelain veneers, tooth jewelry, and comprehensive smile architecture.' },
];

/* ─── Dental FAQ Data ───────────────────────────────────────── */
const FAQS = [
  { q: 'Why choose UK Dental Studio for Dental Implants?', a: 'Our team is led by internationally trained implantologists certified by Nobel Biocare (Switzerland) and members of the International Congress of Oral Implantologists (ICOI). We perform dental implants daily with an exceptional success rate.' },
  { q: 'Is Root Canal Treatment (RCT) painful?', a: 'Not at all! With our advanced single-sitting RCT technology, rotary endodontics, and modern local anesthesia techniques, root canal procedures are completely painless and completed in just one visit.' },
  { q: 'What is the advantage of 3Shape 3D Intraoral Scanning?', a: '3Shape digital scanning replaces uncomfortable traditional mold trays with a fast, high-definition 3D camera. It provides 100% accurate digital impressions for crowns, implants, and aligners without any gagging.' },
  { q: 'How long does orthodontic braces or aligners treatment take?', a: 'Thanks to our ultramodern super-fast low-friction braces and Invisalign clear aligners, treatment time is reduced significantly, achieving your dream smile in less time with maximum comfort.' },
  { q: 'What sterilization protocols do you follow?', a: 'We strictly follow international sterilization and cleanliness protocols. Maximum single-use disposables are used, and non-disposable instruments undergo rigorous multi-stage autoclaving for absolute safety.' },
  { q: 'How do I book an appointment with Dr. Tanya Chauhan?', a: 'You can easily book online via our "Book Appointment" button, call us at +91 95361 13530, or send a message on WhatsApp. We confirm appointments promptly.' },
];

/* ─── Stats Data ─────────────────────────────────────────────── */
const STATS = [
  { icon: '😃', value: 10000, suffix: '+', label: 'Happy Smiles Created', decimal: false },
  { icon: '👩‍⚕️', value: 12, suffix: '+', label: 'Years of Excellence', decimal: false },
  { icon: '💯', value: 99, suffix: '%', label: 'Patient Satisfaction', decimal: false },
  { icon: '🌍', value: 19, suffix: '+', label: 'Countries Patients From', decimal: false },
];

/* ─── AnimatedStat Component ─────────────────────────────────── */
const AnimatedStat = ({ stat }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <div className="stat-card" ref={ref}>
      <span className="stat-icon">{stat.icon}</span>
      <div className="stat-number">
        {inView ? (
          <CountUp end={stat.value} duration={2.5} separator="," />
        ) : '0'}
        <span className="suffix">{stat.suffix}</span>
      </div>
      <div className="stat-label">{stat.label}</div>
    </div>
  );
};

const PRACTICE_IDENTITIES = [
  {
    id: 'general-implant',
    badge: 'General & Implant Practice',
    title: 'Precision & Clinical Excellence',
    subtitle: 'Focused on restoring bite function, structural jaw stability, and lifelong oral health.',
    colorBg: 'linear-gradient(135deg, rgba(0,184,217,0.15) 0%, rgba(11,25,46,0.9) 100%)',
    colorBorder: 'rgba(0,184,217,0.3)',
    pillColor: '#00b8d9',
    icon: '🦷',
    focusPoints: [
      '3Shape 3D Intraoral Digital Scanning',
      'Nobel Biocare (Switzerland) Titanium Implants',
      'Single Sitting Painless Root Canal Therapy (RCT)',
      'Kodak Digital OPG Panoramic Jaw X-Ray'
    ],
    designRationale: 'Deep Navy & Crisp Teal palette signals sterile precision, high medical trust, and immediate pain relief.'
  },
  {
    id: 'cosmetic',
    badge: 'Cosmetic Dentistry & Smile Design',
    title: 'Luxury Aesthetic Architecture',
    subtitle: 'Crafting red-carpet celebrity smiles through porcelain veneers and tooth jewelry.',
    colorBg: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(11,25,46,0.9) 100%)',
    colorBorder: 'rgba(245,158,11,0.3)',
    pillColor: '#f59e0b',
    icon: '💎',
    focusPoints: [
      'Ultra-thin Porcelain Veneers & Luminaires',
      'In-Office Laser Teeth Whitening (45-Min)',
      'Sparkling Tooth Crystals & Diamond Jewelry',
      'Computerized Facial Smile Ratio Analysis'
    ],
    designRationale: 'Warm Gold & Dark Slate palette evokes high-end luxury hospitality, prestige, and visual transformation.'
  },
  {
    id: 'orthodontic',
    badge: 'Orthodontics & Clear Aligners',
    title: 'Alignment & Dentofacial Harmony',
    subtitle: 'Specialized bite correction, invisible aligners, and painless super-fast braces.',
    colorBg: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(11,25,46,0.9) 100%)',
    colorBorder: 'rgba(168,85,247,0.3)',
    pillColor: '#a855f7',
    icon: '✨',
    focusPoints: [
      'Invisalign Clear Invisible Aligners',
      'Super-fast Low Friction Braces',
      'Tooth-colored Ceramic Aesthetic Braces',
      '3D Digital Bite Movement Simulation'
    ],
    designRationale: 'Cyan & Violet gradients dynamic modern aesthetic targeting youth, professionals, and aesthetic conscious patients.'
  },
  {
    id: 'pediatric',
    badge: 'Pediatric Dental Care',
    title: 'Gentle & Anxiety-Free Dentistry',
    subtitle: 'Creating positive early dental experiences with a warm, compassionate family approach.',
    colorBg: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(11,25,46,0.9) 100%)',
    colorBorder: 'rgba(16,185,129,0.3)',
    pillColor: '#10b981',
    icon: '🧸',
    focusPoints: [
      'Pain-free Pediatric Pulpectomy & Fillings',
      'Fluoride Varnish & Pit & Fissure Sealants',
      'Habit Breaking Appliances & Space Maintainers',
      'Warm "Family Wali Feeling" Consultation Environment'
    ],
    designRationale: 'Soft Emerald & Sunny Amber palette lowers dental anxiety and reinforces safety for parents and children.'
  }
];

/* ─── HomePage ───────────────────────────────────────────────── */
const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeIdentityId, setActiveIdentityId] = useState('general-implant');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tRes, bRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/testimonials`),
          axios.get(`${API_BASE_URL}/blog`),
        ]);
        setTestimonials(tRes.data && tRes.data.length > 0 ? tRes.data : STATIC_TESTIMONIALS);
        setBlogs(bRes.data && bRes.data.length > 0 ? bRes.data.slice(0, 3) : STATIC_BLOGS);
      } catch (e) {
        setTestimonials(STATIC_TESTIMONIALS);
        setBlogs(STATIC_BLOGS);
      }
    };
    fetchData();
  }, []);

  const activeIdentity = PRACTICE_IDENTITIES.find(i => i.id === activeIdentityId) || PRACTICE_IDENTITIES[0];

  const slidesPerView = window.innerWidth <= 640 ? 1 : window.innerWidth <= 1024 ? 2 : 3;
  const maxIndex = Math.max(0, testimonials.length - slidesPerView);
  const nextSlide = () => setSliderIndex(i => Math.min(i + 1, maxIndex));
  const prevSlide = () => setSliderIndex(i => Math.max(i - 1, 0));

  useEffect(() => {
    const timer = setInterval(() => {
      setSliderIndex(i => (i >= maxIndex ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [maxIndex]);

  return (
    <>
      {/* ── HERO ── */}
      <section className="hero" id="hero">
        <div className="hero-pattern" />
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <span>✨</span> Top Multispecialty Dental Practice in Haridwar
              </div>
              <h1>
                We Want You to <span className="highlight">Smile</span> — Passion for Excellence
              </h1>
              <p className="hero-description">
                UK Dental Studio combines art with modern dental science. Led by <strong>Dr. Tanya Chauhan</strong> (MDS Orthodontics) & team, we offer 12+ years of clinical excellence, Nobel Biocare implants, painless single-sitting RCT, and 3D digital intraoral scanning.
              </p>
              <div className="hero-actions">
                <Link to="/appointments" className="btn btn-primary" id="hero-book-btn">
                  Book Dental Appointment <FaArrowRight />
                </Link>
                <Link to="/services" className="btn btn-secondary" id="hero-services-btn">
                  Explore Treatments
                </Link>
              </div>
              <div className="hero-stats">
                {[
                  { num: '10k+', label: 'Happy Smiles' },
                  { num: '12+', label: 'Yrs Experience' },
                  { num: '99%', label: 'Satisfaction' },
                ].map(s => (
                  <div className="hero-stat" key={s.label}>
                    <div className="hero-stat-number">{s.num}</div>
                    <div className="hero-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hero-image-side">
              <div className="hero-image-wrapper">
                <img
                  src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=700&h=600&fit=crop"
                  alt="Modern Dental Clinic at Global Dental Practice"
                />
                <div className="hero-card-floating card-1">
                  <div className="card-icon">🔍</div>
                  <div className="card-text">
                    <strong>3Shape 3D Scanner</strong>
                    <span>Digital Precision</span>
                  </div>
                </div>
                <div className="hero-card-floating card-2">
                  <div className="card-icon">⭐</div>
                  <div className="card-text">
                    <strong>Family Wali Feeling</strong>
                    <span>Warm Patient Care</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="stats-section" id="stats">
        <div className="container">
          <div className="stats-grid">
            {STATS.map(stat => <AnimatedStat key={stat.label} stat={stat} />)}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="services-section section-padding" id="services">
        <div className="container">
          <div className="section-header">
            <div className="badge">Our Specialties</div>
            <h2 className="section-title">World-Class Dental Treatments</h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              From preventive care to high-end cosmetic smile architecture and 3D digital implantology — 
              our team delivers painless, ultramodern dental treatments.
            </p>
          </div>
          <div className="services-grid">
            {SERVICES.map(svc => (
              <div className="service-card" key={svc.id} id={`service-${svc.id}`}>
                <div className="service-icon">{svc.icon}</div>
                <h3>{svc.title}</h3>
                <p>{svc.description}</p>
                <Link to={`/services#${svc.id}`} className="service-link">
                  Read More <FaArrowRight style={{ fontSize: '0.75rem' }} />
                </Link>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to="/services" className="btn btn-outline-navy">View All Dental Treatments</Link>
          </div>
        </div>
      </section>

      {/* ── PRACTICE IDENTITY & DESIGN TRENDS SHOWCASE ── */}
      <section className="identity-section section-padding" id="design-concept">
        <div className="container">
          <div className="section-header">
            <div className="badge" style={{ background: 'rgba(0,184,217,0.15)', borderColor: 'rgba(0,184,217,0.3)', color: '#4dd9f0' }}>
              Dental UI/UX Design System
            </div>
            <h2 className="section-title" style={{ color: 'white' }}>Multi-Specialty Visual Identity Adaptations</h2>
            <p className="section-subtitle" style={{ margin: '0 auto', color: 'rgba(255,255,255,0.7)' }}>
              See how modern dental practice design adapts its visual language, color psychology, and component structure across specialized dental domains.
            </p>
          </div>

          <div className="identity-switcher-tabs" id="identity-tabs">
            {PRACTICE_IDENTITIES.map(item => (
              <button
                key={item.id}
                id={`identity-tab-${item.id}`}
                className={`identity-tab ${activeIdentityId === item.id ? 'active' : ''}`}
                onClick={() => setActiveIdentityId(item.id)}
              >
                <span>{item.icon}</span> {item.badge}
              </button>
            ))}
          </div>

          <div
            className="identity-card"
            style={{
              background: activeIdentity.colorBg,
              borderColor: activeIdentity.colorBorder
            }}
          >
            <div>
              <div
                className="identity-color-pill"
                style={{ background: activeIdentity.pillColor, color: 'white' }}
              >
                {activeIdentity.badge}
              </div>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '2rem', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>
                {activeIdentity.title}
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '1.75rem' }}>
                {activeIdentity.subtitle}
              </p>

              <div style={{ background: 'rgba(0,0,0,0.25)', padding: '1.25rem 1.5rem', borderRadius: 'var(--radius-md)', border: '1px solid rgba(255,255,255,0.08)', marginBottom: '1.75rem' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: activeIdentity.pillColor, fontWeight: 700, marginBottom: '0.4rem' }}>
                  🎨 Color & UI Rationale
                </div>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  {activeIdentity.designRationale}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {activeIdentity.focusPoints.map((pt, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', fontSize: '0.85rem' }}>
                    <span style={{ color: activeIdentity.pillColor }}>✓</span> {pt}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '2rem', borderRadius: 'var(--radius-lg)', border: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{activeIdentity.icon}</div>
              <h4 style={{ color: 'white', fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Trust & Authority Focus
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                Designed to eliminate dental anxiety, display verified credentials, and convert visitors into appointments.
              </p>
              <Link to="/appointments" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                Book Consultation Now
              </Link>
            </div>
          </div>

          <div className="trust-proof-bar" id="trust-proof-bar">
            <div className="trust-proof-item">
              <span className="trust-proof-icon">⭐</span>
              <div className="trust-proof-text">
                <strong>4.9/5 Rating (500+ Reviews)</strong>
                <span>Verified Google Patient Feedback</span>
              </div>
            </div>
            <div className="trust-proof-item">
              <span className="trust-proof-icon">🏥</span>
              <div className="trust-proof-text">
                <strong>Nobel Biocare Certified</strong>
                <span>Swiss Premium Dental Implants</span>
              </div>
            </div>
            <div className="trust-proof-item">
              <span className="trust-proof-icon">💎</span>
              <div className="trust-proof-text">
                <strong>3Shape 3D Scanner</strong>
                <span>Top 0.001% Digital Clinics Globally</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DOCTOR FEATURE — DR. TANYA CHAUHAN ── */}
      <section className="doctor-section section-padding" id="featured-doctor">
        <div className="container">
          <div className="doctor-profile-grid">
            <div className="doctor-image-container">
              <div className="doctor-image-wrapper">
                <img
                  src="https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=600&h=600&fit=crop"
                  alt="Dr. Tanya Chauhan - Consultant Orthodontist & Implantologist"
                />
              </div>
              <div className="doctor-experience-badge">
                <span className="years">12+</span>
                <span className="label">Years<br />Experience</span>
              </div>
            </div>

            <div>
              <div className="badge">Meet Our Lead Specialist</div>
              <h2 className="section-title">Dr. Tanya Chauhan</h2>
              <p style={{ color: 'var(--color-teal-dark)', fontWeight: 700, fontSize: '1.1rem', marginBottom: '1rem' }}>
                BDS, MDS (Orthodontics & Dentofacial Orthopedics) — Smile Architect & Implantologist
              </p>
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                Dr. Tanya Chauhan is a renowned Orthodontist, Smile Architect, and Dental Implantologist at UK Dental Studio. 
                Internationally trained in Nobel Biocare Dental Implants (Switzerland) and an esteemed member of the International 
                Congress of Oral Implantologists (ICOI), she combines art with cutting-edge dental science.
              </p>
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.8, marginBottom: '2rem' }}>
                She specializes in painless super-fast low-friction braces, ceramic braces, clear aligners (Invisalign), 
                and advanced implant surgery, crafting healthy, vibrant smiles for patients across India and abroad.
              </p>

              <div className="doctor-credentials">
                {[
                  { icon: '🎓', title: 'BDS, MDS (Orthodontics & Dentofacial Orthopedics)', detail: 'Internationally Trained (Nobel Biocare - Switzerland)' },
                  { icon: '🏆', title: 'Member of ICOI', detail: 'International Congress of Oral Implantologists' },
                  { icon: '✨', title: 'Expert Smile Architect', detail: 'Painless Super-Fast Braces & Clear Aligners Specialist' },
                ].map((c, i) => (
                  <div className="credential-item" key={i}>
                    <span className="credential-icon">{c.icon}</span>
                    <div className="credential-text">
                      <strong>{c.title}</strong>
                      <span>{c.detail}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="doctor-specialties">
                {['Orthodontics', 'Dental Implants', 'Invisalign', 'Smile Design', 'Single Sitting RCT'].map(tag => (
                  <span className="specialty-tag" key={tag}>{tag}</span>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link to="/appointments" className="btn btn-primary">Book Consultation with Dr. Tanya</Link>
                <Link to="/doctors" className="btn btn-outline-navy">Our Dental Team</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="testimonials-section section-padding" id="testimonials-section">
        <div className="container">
          <div className="section-header" style={{ position: 'relative' }}>
            <div className="badge" style={{ background: 'rgba(0,184,217,0.2)', borderColor: 'rgba(0,184,217,0.4)', color: 'var(--color-teal-light)' }}>Patient Reviews</div>
            <h2 className="section-title">Our Happy Clients Say About Us</h2>
            <p className="section-subtitle" style={{ margin: '0 auto', color: 'rgba(255,255,255,0.7)' }}>
              Real patient experiences from Global Dental Clinic ™.
            </p>
          </div>

          {testimonials.length > 0 && (
            <>
              <div className="testimonials-slider" id="testimonials-slider">
                <div
                  className="testimonials-track"
                  style={{ transform: `translateX(calc(-${sliderIndex * (100 / slidesPerView)}% - ${sliderIndex * 1.5}rem))` }}
                >
                  {testimonials.map(t => (
                    <div className="testimonial-card" key={t.id}>
                      <div className="testimonial-treatment">{t.treatmentType || t.treatment_type}</div>
                      <div className="testimonial-stars">
                        {[...Array(t.rating || 5)].map((_, i) => <FaStar key={i} />)}
                      </div>
                      <p className="testimonial-text">"{t.review}"</p>
                      <div className="testimonial-author">
                        <img src={t.avatarUrl || t.avatar_url} alt={t.patientName || t.patient_name} className="testimonial-avatar" />
                        <div className="testimonial-author-info">
                          <strong>{t.patientName || t.patient_name}</strong>
                          <span>{t.location}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="slider-controls">
                <button className="slider-btn" onClick={prevSlide} id="slider-prev"><FaChevronLeft /></button>
                <div className="slider-dots">
                  {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                    <button
                      key={i}
                      className={`slider-dot ${sliderIndex === i ? 'active' : ''}`}
                      onClick={() => setSliderIndex(i)}
                      id={`slider-dot-${i}`}
                    />
                  ))}
                </div>
                <button className="slider-btn" onClick={nextSlide} id="slider-next"><FaChevronRight /></button>
              </div>
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/testimonials" className="btn btn-secondary">Read More Reviews</Link>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="cta-banner" id="cta-banner">
        <div className="container">
          <div className="cta-banner-inner">
            <h2>Smile, You're Designed To!</h2>
            <p>Experience world-class dental care with Dr. Tanya Chauhan & team. Call or book your consultation today.</p>
            <div className="cta-banner-actions">
              <Link to="/appointments" className="btn btn-gold" id="cta-book-btn">
                Book Appointment Now <FaArrowRight />
              </Link>
              <a href="tel:+919536113530" className="btn btn-secondary">
                <FaPlay style={{ fontSize: '0.75rem' }} /> Call: +91 95361 13530
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="faq-section section-padding" id="faq">
        <div className="container">
          <div className="faq-grid">
            <div>
              <div className="badge">Common Questions</div>
              <h2 className="section-title">Frequently Asked Questions</h2>
              <p className="section-subtitle">
                Find answers to common queries regarding dental implants, single-sitting RCT, 
                braces, 3D scanning, and clinic protocols.
              </p>
              <div style={{ marginTop: '2rem' }}>
                <Link to="/contact" className="btn btn-primary">Ask Our Dentists</Link>
              </div>
            </div>

            <div className="faq-list">
              {FAQS.map((faq, i) => (
                <div className={`faq-item ${openFaq === i ? 'open' : ''}`} key={i} id={`faq-item-${i}`}>
                  <div className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    {faq.q}
                    <span className="faq-icon"><FaPlus /></span>
                  </div>
                  <div className={`faq-answer ${openFaq === i ? 'open' : ''}`}>
                    <div className="faq-answer-inner">{faq.a}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOG PREVIEW ── */}
      <section className="blog-section section-padding" id="blog-preview">
        <div className="container">
          <div className="section-header">
            <div className="badge">Oral Health Insights</div>
            <h2 className="section-title">Latest from Our Dental Blog</h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              Expert articles on teeth whitening, oral hygiene maintenance, and smile design from our team.
            </p>
          </div>
          <div className="blog-grid">
            {(blogs.length ? blogs : STATIC_BLOGS).map(post => (
              <div className="blog-card" key={post.id}>
                <div className="blog-card-image">
                  <img src={post.imageUrl || post.image_url} alt={post.title} />
                </div>
                <div className="blog-card-body">
                  <span className="blog-category">{post.category}</span>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <div className="blog-meta">
                    <span>👤 {post.author}</span>
                    <span>📖 {post.readTimeMinutes || post.read_time_minutes} min read</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/blog" className="btn btn-outline-navy">Read All Dental Articles</Link>
          </div>
        </div>
      </section>
    </>
  );
};

/* ─── Static Fallback Data ───────────────────────────────────── */
const STATIC_TESTIMONIALS = [
  { id: 1, patientName: 'Bharat Sharma', location: 'Haridwar, Uttarakhand', review: 'The doctors are now like a family to me. They make us feel so comfortable. Thank you. Its probably the best dental clinic in Haridwar.', rating: 5, treatmentType: 'General Dental Care', avatarUrl: '/images/profile-3-1.png' },
  { id: 2, patientName: 'Bhavik Vadhia', location: 'Haridwar, Uttarakhand', review: 'I had visited the clinic for my dental implant In Haridwar. The experience was really good. I had thought that it would be difficult for me. But they did a very good job.', rating: 5, treatmentType: 'Dental Implant', avatarUrl: '/images/profile-3-1-1.png' },
  { id: 3, patientName: 'Ditika Mahida', location: 'Haridwar, Uttarakhand', review: 'I underwent dental implant here, and believe me the experience is just amazing! Brilliant work by the team, can’t stop ogling my new teeth(got 2).', rating: 5, treatmentType: 'Dental Implants (2 Teeth)', avatarUrl: '/images/profile-3-1-1.png' },
  { id: 4, patientName: 'Hemant Pal', location: 'Haridwar, Uttarakhand', review: 'I had got my implants done at their clinic. The machines you have is really good. But more importantly it’s your nature which wins. Thanks.', rating: 5, treatmentType: 'Dental Implants & 3D Scan', avatarUrl: '/images/profile-3-1-1.png' },
  { id: 5, patientName: 'Reema Kapoor', location: 'Haridwar, Uttarakhand', review: 'Family wali feeling at UK dental studio by Dr.Tanya Chauhan', rating: 5, treatmentType: 'Cosmetic Dentistry', avatarUrl: '/images/download-1.png' },
];

const STATIC_BLOGS = [
  { id: 1, title: 'A Sparkling Smile Like a Pearl with Teeth Jewelry', excerpt: 'Add a twinkle to your smile with non-invasive, safe tooth jewelry. Learn how tooth crystals are painlessly attached.', author: 'Dr. Tanya Chauhan', category: 'Cosmetic Dentistry', imageUrl: '/images/tooth-jewelry.jpg', readTimeMinutes: 4 },
  { id: 2, title: 'What is Oral Hygiene? How to Maintain Optimal Dental Health', excerpt: 'Proper brushing, flossing, and regular dental checkups prevent gum disease and tooth decay.', author: 'Dr. Tanya Chauhan', category: 'Preventive Dentistry', imageUrl: '/images/What-is-Oral-Hygiene.jpg', readTimeMinutes: 5 },
  { id: 3, title: 'Professional Teeth Whitening: Everything You Need to Know', excerpt: 'Brighten your discolored teeth safely with in-office dental bleaching.', author: 'Dr. Tanya Chauhan', category: 'Cosmetic Dentistry', imageUrl: '/images/Teeth-Whitening.jpg', readTimeMinutes: 6 },
];

export default HomePage;
